import type { IconType } from '@kognitos/lattice';

import type {
  IRainToolActivity,
  TRainToolActivityLabelResolver,
} from '../../hooks/types';
import type { TRainValueJs } from '../../utils/value';
import { ESherlockTool } from '../sherlock';
import { asHostLabel, asShortLabel } from './labels';

/** Namespace Sherlock's chat copy lives in. */
const NS = 'finance';

/** A resolver with no argument to echo: the copy is the whole row. */
const staticLabel =
  (step: string): TRainToolActivityLabelResolver =>
  ({ status }) => ({
    key: `finance.investigator.activity.${step}.${status}`,
    ns: NS,
  });

/** A resolver that echoes one argument; no label until it has streamed in. */
const argLabel =
  (
    step: string,
    argName: string,
    format: (value: TRainValueJs | undefined) => string | undefined,
  ): TRainToolActivityLabelResolver =>
  ({ status, args }) => {
    const value = format(args[argName]);
    if (!value) return undefined;
    return {
      key: `finance.investigator.activity.${step}.${status}`,
      ns: NS,
      values: { value },
    };
  };

const row = (
  icon: IconType,
  label: TRainToolActivityLabelResolver,
): IRainToolActivity => ({ icon, prominent: true, label });

const readItem = row('List', staticLabel('readItem'));
const traceRecords = row('Network', staticLabel('traceRecords'));
const checkSync = row('RefreshCw', staticLabel('checkSync'));
const findSource = row('Plug', staticLabel('findSource'));
const checkGuides = row('BookOpen', staticLabel('checkGuides'));

/**
 * Sherlock's tools as business steps. Every tool but the connection picker,
 * which stays a card because the user answers it.
 */
export const SHERLOCK_TOOL_ACTIVITIES: Readonly<
  Record<string, IRainToolActivity>
> = {
  [ESherlockTool.GetFinding]: row('FileText', staticLabel('readFinding')),
  [ESherlockTool.ListFindings]: row('Search', staticLabel('relatedFindings')),
  [ESherlockTool.GetInvestigation]: row('FileText', staticLabel('readFinding')),
  [ESherlockTool.ListInvestigations]: row(
    'Search',
    staticLabel('relatedFindings'),
  ),
  [ESherlockTool.ListDetectors]: row('ScanSearch', staticLabel('readDetector')),
  [ESherlockTool.GetEntity]: traceRecords,
  [ESherlockTool.ListEntities]: traceRecords,
  [ESherlockTool.SearchEntities]: traceRecords,
  [ESherlockTool.LookupEntity]: traceRecords,
  [ESherlockTool.TraverseEntity]: traceRecords,
  [ESherlockTool.GetFact]: traceRecords,
  [ESherlockTool.ListFacts]: traceRecords,
  [ESherlockTool.ListFactHistory]: row('History', staticLabel('recordHistory')),
  [ESherlockTool.FindSharedNeighbors]: traceRecords,
  [ESherlockTool.ReverseResolution]: traceRecords,
  [ESherlockTool.GetOntology]: traceRecords,
  [ESherlockTool.GetConnector]: checkSync,
  [ESherlockTool.ListConnectors]: checkSync,
  [ESherlockTool.GetSyncJob]: checkSync,
  [ESherlockTool.ListSyncJobs]: checkSync,
  [ESherlockTool.ListConnections]: findSource,
  [ESherlockTool.GetConnection]: findSource,
  [ESherlockTool.BrowseDiscoverables]: findSource,
  [ESherlockTool.ListConnectionProcedures]: findSource,
  [ESherlockTool.SearchApps]: findSource,
  [ESherlockTool.SearchProcedures]: findSource,
  [ESherlockTool.SetDiscovered]: findSource,
  [ESherlockTool.PrepareCodeManifest]: row(
    'Database',
    staticLabel('prepareQuery'),
  ),
  [ESherlockTool.RunCode]: row('Database', staticLabel('checkSource')),
  [ESherlockTool.WebSearch]: row(
    'Globe',
    argLabel('webSearch', 'query', asShortLabel),
  ),
  [ESherlockTool.WebFetch]: row(
    'Link',
    argLabel('webFetch', 'url', asHostLabel),
  ),
  [ESherlockTool.GetWorkItem]: readItem,
  [ESherlockTool.ListWorkUnits]: readItem,
  [ESherlockTool.GetWorkUnit]: readItem,
  [ESherlockTool.PublishDecisionForm]: row(
    'MessageSquare',
    staticLabel('publishForm'),
  ),
  [ESherlockTool.ProposeFindingDecision]: row(
    'PencilLine',
    staticLabel('proposeDecision'),
  ),
  [ESherlockTool.SubmitFindingDecisions]: row(
    'SendHorizontal',
    staticLabel('submitDecisions'),
  ),
  [ESherlockTool.SearchGuide]: checkGuides,
  [ESherlockTool.GetGuide]: checkGuides,
  [ESherlockTool.CreateGuide]: row('Bookmark', staticLabel('saveGuide')),
  [ESherlockTool.GetWsAutomations]: row(
    'Workflow',
    staticLabel('lookUpAutomations'),
  ),
  [ESherlockTool.GetAutomation]: row(
    'Workflow',
    staticLabel('readAutomationInputs'),
  ),
};
